import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { EditTreatmentMessageTemplateComponent } from '../../treatment-message-template';
import { ButtonModule } from 'primeng/button';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { EditTreatmentMessageTemplateDialogConfig } from './edit-treatment-message-template-dialog-config';
import { createTreatmentMessageTemplateForm, TreatmentMessageTemplateEditForm } from '../../../core/model';
import { TreatmentMessageTemplateClient } from '../../../core/client';
import { firstValueFrom } from 'rxjs';
import { isEqual, isEqualWith, pick } from 'lodash-es';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { LocaleCodes } from '../../../core/constants';

@Component({
    selector: 'app-edit-treatment-message-template-dialog',
    standalone: true,
    imports: [CommonModule, EditTreatmentMessageTemplateComponent, ButtonModule, ToastModule],
    template: `
        <div *ngIf="form">
            <div class="pt-2"><app-edit-treatment-message-template [form]="form" /></div>

            <div class="d-flex justify-content-end mt-3">
                <p-button
                    class="me-3"
                    styleClass="p-button-sm p-button-primary p-button-secondary p-button-outlined p-button-sm"
                    [outlined]="true"
                    label="取消"
                    [disabled]="isSaving"
                    (onClick)="onCancel()"
                />
                <p-button
                    [loading]="isSaving"
                    [disabled]="form.pristine || form.invalid || isSaving"
                    styleClass="p-button-sm p-button-primary p-button-outlined p-button-sm"
                    [outlined]="true"
                    label="保存"
                    (onClick)="save()"
                />
            </div>
        </div>

        <p-toast />
    `,
    styleUrls: ['./edit-treatment-message-template-dialog.component.scss'],
    providers: [MessageService],
})
export class EditTreatmentMessageTemplateDialogComponent implements OnInit {
    private readonly _client = inject(TreatmentMessageTemplateClient);
    private readonly _dialogConfig =
        inject<DynamicDialogConfig<EditTreatmentMessageTemplateDialogConfig>>(DynamicDialogConfig);
    private readonly _toast = inject(MessageService);

    protected isSaving = false;
    protected readonly ref = inject(DynamicDialogRef, { optional: true });

    protected form?: TreatmentMessageTemplateEditForm;

    ngOnInit() {
        const { _dialogConfig } = this;
        if (_dialogConfig && _dialogConfig.data) {
            this.form = createTreatmentMessageTemplateForm(_dialogConfig.data.instruction.treatmentTypeId!);
            this.form.patchValue(_dialogConfig.data.instruction);
        }
    }

    protected onCancel() {
        this.ref?.close({ saved: false });
    }

    protected async save() {
        const { form, _dialogConfig } = this;
        const models = _dialogConfig.data?.models ?? [];
        if (form != null && form?.valid) {
            this.isSaving = true;

            const formValue = form.getRawValue();
            formValue.locale = formValue.locale ? formValue.locale : LocaleCodes.enUS;

            if (
                models.some(
                    m =>
                        isEqualWith(
                            pick(m, 'region', 'organizationId', 'fabricationMethod', 'hasTreatmentPlan', 'locale', 'isFirstOrSecondOrderOnly'),
                            pick(
                                formValue,
                                'region',
                                'organizationId',
                                'fabricationMethod',
                                'hasTreatmentPlan',
                                'locale',
                                'isFirstOrSecondOrderOnly'
                            ),
                            (l, r) => (l == null && r == null ? true : undefined)
                        ) && m.id !== formValue.id
                )
            ) {
                this.isSaving = false;
                this._toast.add({
                    severity: 'warn',
                    summary: '已存在完全相同的消息模板',
                    closable: true,
                });
                return;
            }

            const saved = await firstValueFrom(this._client.save(formValue)).then(
                () => true,
                () => false
            );
            if (saved) this.ref?.close({ saved, data: formValue });
            this.isSaving = false;
        }
    }
}
